import { ref, watch, onMounted, onUnmounted, type Ref, type ComputedRef } from 'vue';
import type { FilterDescriptor } from '@progress/kendo-data-query';
import type { SettingElementInterface } from '@/types/items';
import { usePagedList, type Page } from '@/composables/usePagedList';
import { usePredicateStore } from '@/stores/predicate-store';

export type FetchResult = Page<SettingElementInterface>;

export interface UseElementGridConfig {
  predicates: ComputedRef<FilterDescriptor[]>;
  filterRefs: Ref<{ stringPartial: string; [key: string]: any }>;
  // `limit` is the session page size (predicateStore.pageSize), null while none is picked. It is
  // the fetchFn's to pass on: the request builder adds it to a first page only (spec 2026-10-16).
  fetchFn: (url: string | null, predicates: FilterDescriptor[], limit: number | null) => Promise<FetchResult>;
  autoFetch?: boolean;       // default true
  searchDebounce?: number;   // default 300ms
}

export function useElementGrid(config: UseElementGridConfig) {
  const predicateStore = usePredicateStore();
  const selectedElement = ref<SettingElementInterface | null>(null);

  // The list itself — latest request wins, paging, refresh — is usePagedList's. The session page
  // size is read as each request is made, not captured here, so a search, a refresh or a reload
  // after a size change all carry the size in force then (spec 2026-10-16).
  const { dataItems, loading, metadata, links, fetch, refresh, onNextPage, onPreviousPage } =
    usePagedList<SettingElementInterface>(
      (url) => config.fetchFn(url, config.predicates.value, predicateStore.pageSize),
    );

  const onSelect = (element: SettingElementInterface) => {
    selectedElement.value = element;
  };

  // The search text is one of the predicates (useElementPredicates turns it into a name
  // predicate), so writing it changes the predicate list and the watcher below would fetch at
  // once — on every keystroke, with the debounced fetch repeating it. The write still happens
  // immediately, so a consumer's filtersActive and Clear see the text straight away; the fetch is
  // the timer's alone, and the watcher is told to let that one change through.
  //
  // The flag is only ever raised together with a freshly started timer, and the watcher run the
  // write causes (the next flush) lowers it, so a suppressed watcher fetch is always followed by
  // the timer's. Two things keep that true: the no-op return in onSearch (an unchanged write never
  // reaches the watcher, and would leave the flag raised), and the timer lowering it too, for a
  // predicate list that happens not to carry the text. A second predicate change in the same tick
  // as the write shares its watcher run and waits for the timer with it — nothing is lost, at
  // most it is late by the debounce.
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let searchWritePending = false;
  const debounceMs = config.searchDebounce ?? 300;

  const cancelSearchTimer = () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = null;
  };

  const onSearch = (event: any) => {
    const value = event?.target?.value ?? '';
    // A keyup that leaves the text as it was (Shift, an arrow key) changes nothing to fetch for —
    // and must not raise the flag, since no watcher run would follow to lower it.
    if (value === config.filterRefs.value.stringPartial) return;
    searchWritePending = true;
    config.filterRefs.value.stringPartial = value;
    cancelSearchTimer();
    searchTimer = setTimeout(() => {
      searchTimer = null;
      searchWritePending = false;
      fetch();
    }, debounceMs);
  };

  watch(config.predicates, () => {
    if (searchWritePending) {
      searchWritePending = false;
      return;
    }
    // Any other change — a chip, Clear, a world — fetches now, and the predicate list already
    // carries the typed text, so a pending search fetch would only repeat it.
    cancelSearchTimer();
    fetch();
  });

  // A new size is a new first page of the list as it stands — the search text and filters stay as
  // they were (spec 2026-10-16) — so it is handled like any other change to what the list asks for:
  // the predicate list already carries the typed text, and a pending search fetch would only repeat
  // it. A first page, not a refresh: the page on screen is a link that carries the limit it was
  // fetched with, so asking for it again would leave the size unchanged. Unlike a finder's browser
  // there is no closed state to leave alone. A list is mounted while it is on screen, and a data
  // manager list also stays mounted under its editor modal (an editor's browsable finders:
  // AncestryEditor's AbilityFinder, the modification configurators' finders), so a size picked there
  // reloads the one list beneath the modal: one list, not a fan-out to lists nobody sees.
  watch(() => predicateStore.pageSize, () => {
    cancelSearchTimer();
    fetch();
  });

  if (config.autoFetch !== false) {
    onMounted(() => { fetch(); });
  }

  onUnmounted(() => {
    cancelSearchTimer();
  });

  return {
    dataItems,
    selectedElement,
    loading,
    metadata,
    links,
    fetch,
    refresh,
    onSelect,
    onSearch,
    onNextPage,
    onPreviousPage,
  };
}
